<template>
  <v-dialog
    :value="value"
    :persistent="!isEnterEmailStep || loading"
    width="400"
    @input="$emit('input', $event)"
  >
    <v-card :disabled="loading" :loading="loading">
      <v-form
        v-model="validForm"
        :disabled="loading"
        @submit.prevent="submitChanges"
      >
        <v-card-title>Edit child email</v-card-title>
        <v-card-text>
          <template v-if="activeChildProfile.email">
            <v-text-field
              v-model="activeChildProfile.email"
              :rules="rules.email"
              readonly
              label="Current email"
              color="black"
              outlined
            />
            <v-text-field
              v-if="!isEnterEmailStep"
              v-model="formData.emailCode"
              :rules="rules.code"
              type="email"
              label="Current email code"
              outlined
            />
          </template>

          <v-text-field
            v-model="formData.newEmail"
            :readonly="!isEnterEmailStep"
            :rules="rules.email"
            :color="isEnterEmailStep ? null : 'black'"
            type="email"
            label="New email"
            outlined
          />
          <v-text-field
            v-if="!isEnterEmailStep"
            v-model="formData.newEmailCode"
            :rules="rules.code"
            type="email"
            label="New email code"
            outlined
          />

          <v-alert
            v-if="!isEnterEmailStep"
            type="info"
            color="black"
            outlined
          >
            <span>Resend code will be available in 1 minute</span>
            <template v-slot:append>
              <v-btn
                :disabled="!!sendCodeUnavailableUntil && !updateCodesSent"
                :text="!!sendCodeUnavailableUntil || updateCodesSent"
                color="blue"
                outlined
                width="104"
                @click="sendCodesToEmail"
              >
                <v-icon v-if="updateCodesSent" left>done</v-icon>
                <span>{{ sendResetCodeText }}</span>
              </v-btn>
            </template>
          </v-alert>
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <v-btn
            v-if="isEnterEmailStep"
            text
            @click="close"
          >
            Close
          </v-btn>
          <v-btn
            v-else
            text
            @click="cancelEdit"
          >
            Cancel
          </v-btn>

          <v-spacer />

          <v-btn
            v-if="isEnterEmailStep"
            :disabled="!validForm || !!sendCodeUnavailableUntil"
            type="submit"
            width="116"
            color="blue"
            class="white--text"
          >
            {{ sendCodeUnavailableUntil ? sendCodeUnavailableTime : 'Send code' }}
          </v-btn>
          <v-btn
            v-else
            :disabled="!validForm"
            type="submit"
            color="blue"
            class="white--text"
          >
            Submit changes
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { isNil, omitBy } from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import { emailRegex } from '@/helpers/regex';
import { useIntervalTimer } from '@/helpers/timers';
import { handleReqErrorWithToast } from '@/helpers/error-handlers';

function getInitialFormData() {
  return {
    newEmail: '',
    emailCode: '',
    newEmailCode: '',
  };
}

export default {
  name: 'EditChildEmailDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      validForm: false,
      loading: false,
      formData: getInitialFormData(),
      rules: {
        email: [
          v => !!v || 'Field is required',
          v => emailRegex.test(v) || 'Invalid email format',
        ],
        code: [
          v => !!v || 'Field is required',
        ],
      },
      isEnterEmailStep: true,
      updateCodesSent: false,
      sendCodeUnavailableUntil: null,
      sendCodeUnavailableTime: null,
    };
  },
  computed: {
    ...mapGetters('children', [
      'activeChildProfile',
    ]),
    alreadyHasEmail() {
      return this.activeChildProfile.email;
    },
    sendResetCodeText() {
      if (this.updateCodesSent) {
        return 'Sent';
      }
      if (this.sendCodeUnavailableUntil) {
        return this.sendCodeUnavailableTime;
      }
      return 'Resend';
    },
  },
  methods: {
    ...mapActions('children', [
      'sendUpdateEmailCodes',
      'updateChildEmail',
    ]),
    submitChanges() {
      if (this.isEnterEmailStep) {
        this.sendCodesToEmail();
      } else {
        this.submitChangeEmail();
      }
    },
    sendCodesToEmail() {
      if (this.updateCodesSent) {
        this.updateCodesSent = false;
        return;
      }
      if (this.sendCodeUnavailableUntil || this.loading) {
        return;
      }

      const successMsg = this.alreadyHasEmail
        ? 'Update email codes sent to your\n current and specified email addresses'
        : 'Update email code sent to the specified email address';
      const errorMsg = this.alreadyHasEmail
        ? 'Failed to send update email codes'
        : 'Failed to send update email code';

      this.loading = true;

      this.sendUpdateEmailCodes({ newEmail: this.formData.newEmail })
        .then(() => {
          this.$toast.success(successMsg);
          this.updateCodesSent = true;
          this.isEnterEmailStep = false;
          this.setSendCodeTimer();
        })
        .catch(err => handleReqErrorWithToast(err, errorMsg))
        .finally(() => (this.loading = false));
    },
    setSendCodeTimer() {
      useIntervalTimer('1m', (secondsLeft, timerUntil) => {
        this.sendCodeUnavailableTime = secondsLeft;
        this.sendCodeUnavailableUntil = timerUntil;
        if (!timerUntil) {
          this.updateCodesSent = false;
        }
      });
    },
    submitChangeEmail() {
      if (this.loading) return;

      this.loading = true;

      const payload = omitBy(this.formData, isNil);
      this.updateChildEmail(payload)
        .then(() => {
          this.$toast.success('Email updated successfully');
          this.cancelEdit();
        })
        .catch(err => handleReqErrorWithToast(err, 'Failed to update email using specified data'))
        .finally(() => (this.loading = false));
    },
    cancelEdit() {
      this.setInitialData();
      this.close();
    },
    setInitialData() {
      this.formData = getInitialFormData();
      this.isEnterEmailStep = true;
    },
    close() {
      this.$emit('input', false);
    },
  },
};
</script>
